import { useState, useEffect, useRef } from "react";
import { useAppTheme, THEMES, NAVY, ACCENT, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";

const API = "/api";

const PRESETS = [
  { key: "today", label: "Täna" },
  { key: "yesterday", label: "Eile" },
  { key: "week", label: "Nädal" },
  { key: "month", label: "Kuu" },
];

const ALL_PRESET = { key: "all", label: "Kõik" };

const MODULE_DEFAULTS = {
  tootunnid: false,
  masinatunnid: undefined,
  paevaaruanded: false,
  mehhanismid: false,
  tarnelehed: false,
  import_remato: false,
  import_waybiller: false,
};

// ─── helpers ───

function fmt(n, unit) {
  const rounded = Math.round(n * 100) / 100;
  const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  const [int, dec] = str.split(".");
  const joined = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + (dec ? "." + dec : "");
  return unit ? `${joined} ${unit}` : joined;
}

function groupByReg(rows) {
  const byReg = {};
  for (const r of rows) {
    if (!byReg[r.reg]) byReg[r.reg] = { reg: r.reg, trips: 0, total: 0 };
    byReg[r.reg].trips += 1;
    byReg[r.reg].total += r.qty;
  }
  return Object.values(byReg).sort((a, b) => b.total - a.total);
}

// "01.09.2026" + "24.09.2026" -> "01.09 – 24.09.2026"; same day -> "24.09.2026"
function rangeLabel(from, to) {
  if (!from || !to) return "";
  if (from === to) return to;
  const sameYear = from.slice(6) === to.slice(6);
  return `${sameYear ? from.slice(0, 5) : from} – ${to}`;
}

function groupLoadsByMaterial(loads) {
  const byMat = {};
  for (const l of loads) {
    if (!byMat[l.material]) byMat[l.material] = { material: l.material, totalLoads: 0, rows: [] };
    byMat[l.material].totalLoads += l.loads;
    byMat[l.material].rows.push(l);
  }
  return Object.values(byMat).sort((a, b) => b.totalLoads - a.totalLoads);
}

function buildSource(id, label, rawMaterials, exportEndpoint, opts = {}) {
  const materials = rawMaterials
    .filter(m => m.total > 0)
    .map(m => ({ ...m, vehicles: new Set(m.rows.map(r => r.reg)).size }));
  const heroUnit = opts.heroUnit || "t";
  const total = heroUnit === "t"
    ? materials.filter(m => m.unit === "t").reduce((s, m) => s + m.total, 0)
    : materials.reduce((s, m) => s + m.total, 0);
  const vehicleCount = new Set(materials.flatMap(m => m.rows.map(r => r.reg))).size;
  const dupMaxScore = Math.max(0, ...materials.flatMap(m => m.rows.map(r => r.dup_score || 0)));
  const hasConflict = materials.some(m => m.rows.some(r => r.dup_conflict));
  return { id, label, materials, total, vehicleCount, exportEndpoint, hasData: materials.length > 0, heroUnit, columns: opts.columns, dupMaxScore, hasConflict };
}

function useLocalTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_theme");
      if (saved) return saved === "dark";
    } catch {}
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const toggle = () => setDark(d => {
    try { localStorage.setItem("dashboard_theme", d ? "light" : "dark"); } catch {}
    return !d;
  });
  return [dark, toggle];
}

// ─── small components ───

function Chevron({ open, color, size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: "transform .15s ease", flexShrink: 0 }}>
      <path d="M9 6l6 6-6 6" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExcelButton({ t, endpoint, params, compact }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); window.open(`${API}/${endpoint}?${params}`, "_blank"); }}
      aria-label="Ekspordi Excel"
      style={{
        fontSize: 11.5, fontWeight: 600, color: compact ? t.muted : t.text, background: "transparent",
        border: `1px solid ${t.border}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 5, fontFamily: "inherit",
      }}>⭳ Excel</button>
  );
}

function PdfButton({ t, href }) {
  return (
    <a href={href} download onClick={e => e.stopPropagation()} aria-label="Lae PDF" style={{
      fontSize: 11.5, fontWeight: 600, color: t.muted, border: `1px solid ${t.border}`, borderRadius: 6,
      padding: "5px 10px", textDecoration: "none", whiteSpace: "nowrap",
    }}>⭳ PDF</a>
  );
}

function DataTable({ t, columns, rows, mobile }) {
  const fs = mobile ? 11 : 12.5;
  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, overflowX: "auto" }}>
      <table style={{ width: "100%", fontSize: fs, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: NAVY }}>
            {columns.map((c, i) => (
              <th key={i} style={{ textAlign: "left", padding: mobile ? "7px 9px" : "8px 12px", fontWeight: 600, color: "#fff", fontSize: mobile ? 10 : 11.5, whiteSpace: "nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: mobile ? "7px 9px" : "8px 12px", color: t.text, borderTop: `1px solid ${t.rowBorder}`, whiteSpace: "nowrap" }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── header ───

function Header({ t, mobile, dark, toggleDark, range, setRange, site, setSite, sites, label, customFrom, customTo, applyCustom, onToggleSidebar, siteLocked }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [from, setFrom] = useState(customFrom);
  const [to, setTo] = useState(customTo);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen]);

  const validRange = from && to && from <= to;

  const presetBtn = p => {
    const active = range === p.key;
    return (
      <button key={p.key} onClick={() => setRange(p.key)} style={{
        padding: mobile ? "6px 11px" : "6px 12px", border: "none", borderRadius: 6,
        background: active ? "#ffffff" : mobile ? "rgba(255,255,255,0.1)" : "transparent",
        color: active ? NAVY : "rgba(255,255,255,0.8)",
        fontSize: mobile ? 11.5 : 12.5, fontWeight: active ? 600 : 500,
        cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "inherit",
      }}>{p.label}</button>
    );
  };

  const siteSelect = siteLocked ? (
    <div style={{
      background: "rgba(255,255,255,0.1)", color: "#fff", borderRadius: 7,
      padding: mobile ? "7px 10px" : "7px 12px", fontSize: mobile ? 11.5 : 12.5,
      fontWeight: 600, letterSpacing: 0.5,
    }}>{site}</div>
  ) : (
    <select value={site} onChange={e => setSite(e.target.value)} style={{
      appearance: "none", WebkitAppearance: "none", background: "rgba(255,255,255,0.1)", color: "#fff", border: "none",
      borderRadius: 7, padding: mobile ? "7px 26px 7px 10px" : "7px 28px 7px 12px",
      fontSize: mobile ? 11.5 : 12.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
      flex: mobile ? 1 : "none", minWidth: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat", backgroundPosition: `right ${mobile ? 8 : 10}px center`,
    }}>
      <option value="" style={{ color: "#000" }}>Kõik objektid</option>
      {sites.map(s => <option key={s.id} value={s.name} style={{ color: "#000" }}>{s.name}</option>)}
    </select>
  );

  const dateChip = (
    <div ref={pickerRef} style={{ position: "relative", flexShrink: 0 }}>
      <button onClick={() => { setFrom(customFrom); setTo(customTo); setPickerOpen(o => !o); }} style={{
        fontSize: mobile ? 10.5 : 12, fontWeight: 500, whiteSpace: "nowrap",
        color: range === "custom" ? "#fff" : "rgba(255,255,255,0.8)",
        padding: mobile ? "7px 10px" : "7px 12px",
        background: range === "custom" ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.1)",
        border: "none", borderRadius: 7, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontFamily: "inherit",
      }}>{label || "…"} <span style={{ fontSize: 9 }}>▾</span></button>
      {pickerOpen && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 20,
          background: t.panel, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14,
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 10, minWidth: 220,
        }}>
          {[["Alates", from, setFrom], ["Kuni", to, setTo]].map(([lbl, val, set]) => (
            <label key={lbl} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10.5, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: t.muted }}>
              {lbl}
              <input type="date" value={val} onChange={e => set(e.target.value)} style={{
                background: t.bg, color: t.text, border: `1px solid ${t.border}`, borderRadius: 6,
                padding: "7px 9px", fontSize: 13, fontFamily: "inherit", colorScheme: dark ? "dark" : "light",
              }} />
            </label>
          ))}
          {from && to && from > to && <div style={{ fontSize: 11, color: t.error }}>Algus peab olema enne lõppu.</div>}
          <button disabled={!validRange} onClick={() => { applyCustom(from, to); setPickerOpen(false); }} style={{
            background: validRange ? ACCENT : t.border, color: validRange ? "#fff" : t.mutedLight, border: "none", borderRadius: 6,
            padding: "8px 12px", fontSize: 12.5, fontWeight: 600, cursor: validRange ? "pointer" : "not-allowed", fontFamily: "inherit",
          }}>Rakenda</button>
        </div>
      )}
    </div>
  );

  const themeBtn = (
    <button onClick={toggleDark} aria-label="Vaheta värviteema" style={{
      width: mobile ? 28 : 30, height: mobile ? 28 : 30, border: "none", borderRadius: 7,
      background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: mobile ? 12 : 13, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>{dark ? "☀" : "☾"}</button>
  );

  const logo = (
    <div style={{ display: "flex", alignItems: "center", gap: mobile ? 8 : 10, marginRight: mobile ? 0 : "auto" }}>
      {onToggleSidebar && !mobile && (
        <button onClick={onToggleSidebar} style={{
          background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", padding: "4px 8px",
        }}>☰</button>
      )}
      <div style={{
        width: mobile ? 28 : 32, height: mobile ? 28 : 32, borderRadius: mobile ? 7 : 8, background: "rgba(255,255,255,0.14)",
        display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: mobile ? 13 : 15, flexShrink: 0,
      }}>S</div>
      <div style={{ lineHeight: 1.3 }}>
        <div style={{ fontWeight: 700, fontSize: mobile ? 12 : 13, letterSpacing: "0.02em" }}>STEINMANN OÜ</div>
        <div style={{ fontSize: mobile ? 10 : 11, color: "rgba(255,255,255,0.72)" }}>LogSite · Ülevaade</div>
      </div>
    </div>
  );

  const shell = { position: "sticky", top: 0, zIndex: 10, background: NAVY, color: "#fff" };

  if (mobile) {
    return (
      <div style={{ ...shell, display: "flex", flexDirection: "column", gap: 9, padding: "10px 14px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>{logo}{themeBtn}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, overflowX: "auto", paddingBottom: 1 }}>
          {PRESETS.map(presetBtn)}
          <span style={{ width: 1, alignSelf: "stretch", margin: "3px 2px", background: "rgba(255,255,255,0.25)", flexShrink: 0 }} />
          {presetBtn(ALL_PRESET)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{siteSelect}{dateChip}</div>
      </div>
    );
  }
  return (
    <div style={{ ...shell, display: "flex", alignItems: "center", gap: 18, padding: "10px 24px", flexWrap: "wrap" }}>
      {logo}
      <div style={{ display: "flex", alignItems: "center", gap: 2, background: "rgba(255,255,255,0.1)", borderRadius: 9, padding: 3 }}>
        {PRESETS.map(presetBtn)}
      </div>
      <div style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,0.1)", borderRadius: 9, padding: 3, marginLeft: -8 }}>
        {presetBtn(ALL_PRESET)}
      </div>
      {siteSelect}
      {themeBtn}
      {dateChip}
    </div>
  );
}

// ─── source (hero) card ───

function MaterialRow({ t, m, mobile, expanded, onToggle, rowsOpen, onToggleRows, columns, showDupFlags, onDupClick }) {
  const regGroups = groupByReg(m.rows);
  const cellPad = mobile ? "6px 8px" : "6px 10px";
  const th = (align) => ({
    position: "sticky", top: 0, background: t.tableHead, textAlign: align, padding: cellPad,
    fontWeight: 600, color: t.muted, borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
  });
  const td = (extra) => ({ padding: cellPad, color: t.text, borderBottom: `1px solid ${t.rowBorder}`, whiteSpace: "nowrap", ...extra });
  const sectionLabel = { fontSize: mobile ? 10 : 10.5, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: t.mutedLight, marginBottom: 6 };

  const header = mobile ? (
    <button onClick={onToggle} style={{
      all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%", display: "flex", flexDirection: "column",
      gap: 3, padding: "12px 0", borderTop: `1px solid ${t.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, flex: 1 }}>
          <Chevron open={expanded} color={t.mutedLight} size={11} />
          <div style={{ fontSize: 12.5, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, textAlign: "left" }} title={m.name}>{m.name}</div>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>{fmt(m.total, m.unit)}</div>
      </div>
      <div style={{ fontSize: 10.5, color: t.mutedLight, paddingLeft: 18 }}>{m.vehicles} autot</div>
    </button>
  ) : (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "11px 0", borderTop: `1px solid ${t.border}` }}>
      <button onClick={onToggle} style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <Chevron open={expanded} color={t.mutedLight} />
        <div style={{ fontSize: 13.5, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, textAlign: "left" }} title={m.name}>{m.name}</div>
        <div style={{ fontSize: 11.5, color: t.mutedLight, whiteSpace: "nowrap", flexShrink: 0 }}>{m.vehicles} autot</div>
      </button>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>{fmt(m.total, m.unit)}</div>
    </div>
  );

  return (
    <div>
      {header}
      {expanded && (
        <div style={{ padding: mobile ? "4px 0 14px 17px" : "6px 0 16px 20px" }}>
          <div style={sectionLabel}>Kokku reg-numbri kaupa</div>
          <div style={{ maxHeight: mobile ? 200 : 220, overflowY: "auto", border: `1px solid ${t.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", fontSize: mobile ? 11 : 12, borderCollapse: "collapse" }}>
              <thead><tr><th style={th("left")}>Reg</th><th style={th("right")}>Sõite</th><th style={th("right")}>Kogus</th></tr></thead>
              <tbody>
                {regGroups.map(g => (
                  <tr key={g.reg}>
                    <td style={td({ color: ACCENT, fontWeight: 600 })}>{g.reg}</td>
                    <td style={td({ textAlign: "right" })}>{g.trips}</td>
                    <td style={td({ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" })}>{fmt(g.total, m.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={onToggleRows} style={{
            all: "unset", boxSizing: "border-box", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            marginTop: mobile ? 10 : 12, fontSize: mobile ? 10.5 : 11, fontWeight: 600, color: ACCENT,
          }}>
            <Chevron open={rowsOpen} color={ACCENT} size={mobile ? 9 : 10} />
            {rowsOpen ? "Peida üksikveosed" : `Näita üksikveoseid (${m.rows.length})`}
          </button>

          {rowsOpen && (() => {
            const cols = columns || [
              { header: "Kuupäev", field: "date", align: "left" },
              { header: "Kell", field: "time", align: "left", hideOnMobile: true },
              { header: "Reg", field: "reg", align: "left", accent: true },
              { header: "Sihtkoht", field: "to", align: "left", hideOnMobile: true, ellipsis: true },
              { header: "Kogus", field: "qty", align: "right", bold: true, fmtUnit: true },
            ];
            const visibleCols = cols.filter(c => !(mobile && c.hideOnMobile));
            const hasDups = showDupFlags && m.rows.some(r => r.dup_conflict || r.dup_score >= 70);
            const hasPdf = m.rows.some(r => r._source === "waybiller" && r._id);
            return (
              <>
                <div style={{ ...sectionLabel, marginTop: 8 }}>Üksikveosed</div>
                <div style={{ maxHeight: 240, overflow: "auto", border: `1px solid ${t.border}`, borderRadius: 8 }}>
                  <table style={{ width: "100%", fontSize: mobile ? 10.5 : 11.8, borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {visibleCols.map((c, i) => <th key={i} style={th(c.align)}>{c.header}</th>)}
                        {hasDups && <th style={th("center")}>Dup</th>}
                        {hasPdf && <th style={th("center")}>Leht</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {m.rows.map((r, i) => (
                        <tr key={i}>
                          {visibleCols.map((c, ci) => (
                            <td key={ci} style={td({
                              textAlign: c.align,
                              ...(c.accent ? { color: ACCENT, fontWeight: 500 } : {}),
                              ...(c.bold ? { fontWeight: 600, fontVariantNumeric: "tabular-nums" } : {}),
                              ...(c.ellipsis ? { overflow: "hidden", textOverflow: "ellipsis", maxWidth: 170 } : {}),
                            })} title={c.ellipsis ? r[c.field] : undefined}>
                              {c.fmtUnit ? fmt(r[c.field], m.unit) : r[c.field]}
                            </td>
                          ))}
                          {hasDups && <td style={td({ textAlign: "center" })}>{r.dup_conflict || r.dup_score >= 70 ? <DupTriangle score={r.dup_score} conflict={r.dup_conflict} source={r._source} recordId={r._id} onClick={onDupClick} /> : null}</td>}
                          {hasPdf && <td style={td({ textAlign: "center" })}>
                            <a href={`${API}/waybiller-deliveries/${r._id}/pdf`} download title="Veoselehe PDF"
                              style={{ color: ACCENT, fontWeight: 600, textDecoration: "none" }}>PDF</a>
                          </td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function groupMaterialsByOrigin(materials) {
  const allOrigins = [...new Set(materials.flatMap(m => m.rows.map(r => r.origin)).filter(Boolean))].sort();
  if (allOrigins.length <= 1) return null;
  const hasEmpty = materials.some(m => m.rows.some(r => !r.origin));
  const originList = hasEmpty ? [...allOrigins, null] : allOrigins;
  return originList.map(origin => {
    const filtered = materials.map(m => {
      const rows = m.rows.filter(r => origin ? r.origin === origin : !r.origin);
      if (rows.length === 0) return null;
      const total = rows.reduce((s, r) => s + (r.qty || 0), 0);
      return { ...m, rows, total, vehicles: new Set(rows.map(r => r.reg)).size };
    }).filter(Boolean);
    const total = filtered.filter(m => m.unit === "t").reduce((s, m) => s + m.total, 0);
    const vehicleCount = new Set(filtered.flatMap(m => m.rows.map(r => r.reg))).size;
    return { origin: origin || "Määramata", materials: filtered, total, vehicleCount };
  }).filter(g => g.materials.length > 0);
}

const DUP_FIELD_LABELS = {
  reg: "Reg", time: "Kellaaeg", qty: "Kogus", material: "Materjal", origin: "Päritolu",
  company: "Ettevõte", driver: "Juht", origin_changed: "Karjäär vahetus", fast_cycle: "Liiga kiire ring",
};

function DupTriangle({ score, conflict, size = 14, source, recordId, onClick }) {
  if (!conflict && (!score || score < 70)) return null;
  const color = conflict || score >= 90 ? "#e53e3e" : "#dd6b20";
  const icon = conflict ? "⛔" : "⚠";
  const title = conflict ? "Võimatu ajagraafik" : `Kattuvus ${score}%`;
  if (!onClick && !source) return <span style={{ color, fontSize: size, lineHeight: 1 }} title={title}>{icon}</span>;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick?.(source, recordId); }}
      style={{ all: "unset", cursor: "pointer", color, fontSize: size, lineHeight: 1 }}
      title={`${title} — kliki detailide jaoks`}>{icon}</button>
  );
}

function DupModal({ t, source, recordId, onClose, mobile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    fetch(`${API}/duplicates/pairs?source=${source}&record_id=${recordId}`, { credentials: "include" })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [source, recordId]);

  const handleDismiss = async (flagId) => {
    const r = await fetch(`${API}/duplicates/${flagId}/dismiss`, { method: "POST", credentials: "include" });
    if (r.ok) setDismissed(prev => new Set([...prev, flagId]));
  };


  const renderRecord = (rec) => (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", fontSize: mobile ? 11 : 12, color: t.text }}>
      <span style={{ color: t.muted, fontWeight: 600 }}>Allikas</span><span>{rec.source === "waybiller" ? "Waybiller" : "Verston"}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Kuupäev</span><span>{rec.date} {rec.time}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Reg</span><span style={{ color: ACCENT, fontWeight: 600 }}>{rec.reg}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Materjal</span><span>{rec.material}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Kogus</span><span style={{ fontWeight: 600 }}>{rec.qty}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Päritolu</span><span>{rec.origin || "—"}</span>
      <span style={{ color: t.muted, fontWeight: 600 }}>Ettevõte</span><span>{rec.company || "—"}</span>
      {rec.waybill_number && <><span style={{ color: t.muted, fontWeight: 600 }}>Saateleht</span><span>{rec.waybill_number}</span></>}
      {rec.external_id && <><span style={{ color: t.muted, fontWeight: 600 }}>Verston ID</span><span>{rec.external_id}</span></>}
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: t.panel, border: `1px solid ${t.border}`, borderRadius: 14,
        padding: mobile ? 18 : 24, maxWidth: 560, width: "100%", maxHeight: "80vh", overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: mobile ? 14 : 16, fontWeight: 700, color: t.text }}>Duplikaadid</div>
          <button onClick={onClose} style={{ all: "unset", cursor: "pointer", fontSize: 18, color: t.muted, padding: 4 }}>✕</button>
        </div>

        {loading ? <div style={{ color: t.muted, fontSize: 13 }}>Laen...</div> : !data?.record ? <div style={{ color: t.muted }}>Andmeid ei leitud</div> : (
          <>
            <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10, padding: mobile ? 12 : 14, marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", marginBottom: 8 }}>See kirje</div>
              {renderRecord(data.record)}
            </div>

            {data.pairs.filter(p => !dismissed.has(p.flag_id)).length === 0 ? (
              <div style={{ color: t.muted, fontSize: 13, textAlign: "center", padding: 16 }}>Kõik duplikaadid on tühistatud</div>
            ) : data.pairs.filter(p => !dismissed.has(p.flag_id)).map(p => (
              <div key={p.flag_id} style={{
                border: `1px solid ${p.score >= 90 ? "#e53e3e44" : "#dd6b2044"}`,
                borderLeft: `3px solid ${p.score >= 90 ? "#e53e3e" : "#dd6b20"}`,
                borderRadius: 10, padding: mobile ? 12 : 14, marginBottom: 10, background: t.bg,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: p.kind === "conflict" || p.score >= 90 ? "#e53e3e" : "#dd6b20", fontWeight: 800, fontSize: mobile ? 14 : 16, whiteSpace: "nowrap" }}>
                      {p.kind === "conflict" ? `⛔ ${p.gap_minutes} min` : `⚠ ${p.score}%`}
                    </span>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(p.matched_fields || []).map(f => (
                        <span key={f} style={{
                          fontSize: 10, fontWeight: 600, background: p.score >= 90 ? "#e53e3e18" : "#dd6b2018",
                          color: p.score >= 90 ? "#e53e3e" : "#dd6b20", padding: "2px 6px", borderRadius: 4,
                        }}>{DUP_FIELD_LABELS[f] || f}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <a href={`${API}/duplicates/${p.flag_id}/pdf`} download style={{
                    fontSize: mobile ? 11 : 12, fontWeight: 600, color: t.muted, padding: "4px 10px",
                    borderRadius: 6, border: `1px solid ${t.border}`, textDecoration: "none",
                  }}>PDF</a>
                  <button onClick={() => handleDismiss(p.flag_id)} style={{
                    all: "unset", cursor: "pointer", fontSize: mobile ? 11 : 12, fontWeight: 600,
                    color: t.muted, padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.border}`,
                  }}>Tühista</button>
                  </div>
                </div>
                {p.kind === "conflict" && (
                  <div style={{ fontSize: mobile ? 11 : 12, color: "#e53e3e", marginBottom: 8 }}>
                    Vahe {p.gap_minutes} min, tavaline ring ~{p.expected_minutes} min
                  </div>
                )}
                {renderRecord(p.record)}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function SourceCard({ t, src, mobile, accentSoft, expanded, toggleMaterial, rowsOpen, toggleRows, exportParams, showDupFlags }) {
  const originGroups = groupMaterialsByOrigin(src.materials);
  const [dupModal, setDupModal] = useState(null);
  const handleDupClick = (source, recordId) => setDupModal({ source, recordId });

  const renderMaterials = (materials, keyPrefix) => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {materials.map(m => {
        const key = `${keyPrefix}::${m.name}`;
        return (
          <MaterialRow key={key} t={t} m={m} mobile={mobile} columns={src.columns}
            expanded={!!expanded[key]} onToggle={() => toggleMaterial(key)}
            rowsOpen={!!rowsOpen[key]} onToggleRows={() => toggleRows(key)}
            showDupFlags={showDupFlags} onDupClick={handleDupClick} />
        );
      })}
    </div>
  );

  return (
    <div style={{
      background: t.panel, border: `1px solid ${t.border}`, borderRadius: mobile ? 12 : 14,
      padding: mobile ? "18px 16px" : "26px 24px", boxShadow: "0 1px 2px rgba(0,0,0,0.06)", minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: mobile ? 8 : 10, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <div style={{ fontSize: mobile ? 11 : 12, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{src.label}</div>
          {showDupFlags && (src.dupMaxScore >= 70 || src.hasConflict) && (
            <button onClick={() => { const base = window.location.pathname.startsWith("/beta") ? "/beta" : ""; window.location.href = base + "/duplicates"; }}
              style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              <DupTriangle score={src.dupMaxScore} conflict={src.hasConflict} size={mobile ? 13 : 15} />
              <span style={{ fontSize: mobile ? 9 : 10, color: src.hasConflict || src.dupMaxScore >= 90 ? "#e53e3e" : "#dd6b20", fontWeight: 600 }}>Raport →</span>
            </button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {src.id === "waybiller" && <PdfButton t={t} href={`${API}/waybiller-deliveries/report.pdf?${exportParams}`} />}
          {!mobile && src.exportEndpoint && <ExcelButton t={t} endpoint={src.exportEndpoint} params={exportParams} compact />}
          <div style={{
            fontSize: mobile ? 15.5 : 17, fontWeight: 700, color: ACCENT, background: accentSoft,
            padding: mobile ? "4px 12px" : "5px 14px", borderRadius: 22, whiteSpace: "nowrap",
          }}>{src.vehicleCount} autot</div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: mobile ? 6 : 7, margin: mobile ? "8px 0 12px" : "10px 0 16px" }}>
        <div style={{ fontSize: mobile ? 32 : 42, fontWeight: 800, color: t.text, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em", lineHeight: 1 }}>{fmt(src.total, "")}</div>
        <div style={{ fontSize: mobile ? 14 : 16, fontWeight: 600, color: t.mutedLight }}>{src.heroUnit || "t"}</div>
      </div>

      {originGroups ? (
        <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 4 : 6 }}>
          {originGroups.map(og => {
            const originKey = `${src.id}::origin::${og.origin}`;
            const isOpen = expanded[originKey] !== false && expanded[originKey] !== undefined ? !!expanded[originKey] : false;
            return (
              <div key={og.origin} style={{
                border: `1px solid ${t.border}`, borderRadius: mobile ? 8 : 10,
                padding: mobile ? "10px 12px" : "12px 16px", background: t.bg,
              }}>
                <button onClick={() => toggleMaterial(originKey)} style={{
                  all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: mobile ? 7 : 8, minWidth: 0, flex: 1 }}>
                    <Chevron open={isOpen} color={ACCENT} size={mobile ? 10 : 11} />
                    <div style={{
                      fontSize: mobile ? 12 : 13.5, fontWeight: 700, color: t.text,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, textAlign: "left",
                    }}>{og.origin}</div>
                    {showDupFlags && <DupTriangle score={Math.max(0, ...og.materials.flatMap(mm => mm.rows.map(r => r.dup_score || 0)))} conflict={og.materials.some(mm => mm.rows.some(r => r.dup_conflict))} size={mobile ? 11 : 12} />}
                    <div style={{
                      fontSize: mobile ? 10 : 11, color: t.mutedLight, whiteSpace: "nowrap", flexShrink: 0,
                    }}>{og.vehicleCount} autot</div>
                  </div>
                  <div style={{
                    fontSize: mobile ? 14 : 16, fontWeight: 800, color: t.text,
                    fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0,
                  }}>{fmt(og.total, src.heroUnit || "t")}</div>
                </button>
                {isOpen && renderMaterials(og.materials, `${src.id}::${og.origin}`)}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {src.materials.map(m => {
            const key = `${src.id}::${m.name}`;
            return (
              <MaterialRow key={key} t={t} m={m} mobile={mobile} columns={src.columns}
                expanded={!!expanded[key]} onToggle={() => toggleMaterial(key)}
                rowsOpen={!!rowsOpen[key]} onToggleRows={() => toggleRows(key)}
                showDupFlags={showDupFlags} onDupClick={handleDupClick} />
            );
          })}
        </div>
      )}
      {dupModal && <DupModal t={t} mobile={mobile} source={dupModal.source} recordId={dupModal.recordId} onClose={() => setDupModal(null)} />}
    </div>
  );
}

// ─── module (lower list) card ───

// ─── haul report per vehicle ───

function HaulReportPanel({ t, mobile, paramStr }) {
  const [vehicles, setVehicles] = useState(null);
  const [picked, setPicked] = useState([]);
  const [extra, setExtra] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setVehicles(null); setErr("");
    fetch(`${API}/hauls/vehicles?${paramStr}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setVehicles(d.vehicles))
      .catch(() => setErr("Autode nimekirja ei saanud laadida."));
  }, [paramStr]);

  const norm = s => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const toggle = reg => setPicked(p => p.includes(reg) ? p.filter(r => r !== reg) : [...p, reg]);
  const addTyped = () => {
    const regs = extra.split(/[\s,;]+/).map(norm).filter(Boolean);
    setPicked(p => [...p, ...regs.filter(r => !p.includes(r))]);
    setExtra("");
  };
  const href = `${API}/hauls/report.pdf?${paramStr}&regs=${encodeURIComponent(picked.join(","))}`;
  const chip = on => ({
    fontSize: mobile ? 12 : 12.5, fontWeight: 600, padding: "6px 10px", borderRadius: 18, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${on ? ACCENT : t.border}`, background: on ? ACCENT : "transparent", color: on ? "#fff" : t.text,
    fontVariantNumeric: "tabular-nums",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 10 }}>
      <div style={{ fontSize: 12.5, color: t.muted }}>
        Vali autod — PDF koondab nende kõik veod valitud perioodil (Waybiller, Verston, tarnija lehed, operaatori märked), iga auto eraldi lehtedel.
      </div>
      {err && <div style={{ color: t.error, fontSize: 12.5 }}>{err}</div>}
      {!vehicles && !err && <div style={{ color: t.muted, fontSize: 12.5 }}>Laadin...</div>}
      {vehicles && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {vehicles.length === 0 && <span style={{ color: t.mutedLight, fontSize: 12.5 }}>Sel perioodil vedusid pole.</span>}
          {vehicles.map(v => (
            <button key={v.reg} onClick={() => toggle(v.reg)} style={chip(picked.includes(v.reg))}>
              {v.reg} <span style={{ opacity: 0.7, fontWeight: 500 }}>· {v.hauls}</span>
            </button>
          ))}
          {picked.filter(r => !(vehicles || []).some(v => v.reg === r)).map(r => (
            <button key={r} onClick={() => toggle(r)} style={chip(true)}>{r} ✕</button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={extra} onChange={e => setExtra(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addTyped(); }}
          placeholder="Lisa numbrimärk (nt 349JXP)"
          style={{ fontSize: 13, padding: "7px 10px", borderRadius: 7, border: `1px solid ${t.border}`, background: t.bg, color: t.text, minWidth: 0, flex: mobile ? "1 1 160px" : "0 1 240px" }} />
        <button onClick={addTyped} style={{ fontSize: 12.5, fontWeight: 600, padding: "7px 12px", borderRadius: 7, border: `1px solid ${t.border}`, background: "transparent", color: t.text, cursor: "pointer", fontFamily: "inherit" }}>Lisa</button>
        {picked.length > 0 && (
          <button onClick={() => setPicked([])} style={{ fontSize: 12.5, padding: "7px 10px", borderRadius: 7, border: "none", background: "transparent", color: t.muted, cursor: "pointer", fontFamily: "inherit" }}>Tühjenda</button>
        )}
        <a href={picked.length ? href : undefined} download aria-disabled={!picked.length}
          style={{
            marginLeft: mobile ? 0 : "auto", fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 7, textDecoration: "none",
            background: picked.length ? ACCENT : t.border, color: "#fff", pointerEvents: picked.length ? "auto" : "none", whiteSpace: "nowrap",
          }}>⭳ PDF{picked.length ? ` (${picked.length} autot)` : ""}</a>
      </div>
    </div>
  );
}

// ─── run ingestors once (admins) ───

const SOURCE_NAMES = { waybiller: "Waybiller", verston: "Verston", infrafly: "Infrafly", navirec: "Navirec", remato: "Remato", ingestor: "Ingestor" };

function IngestRunButton({ t, mobile, onDone }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const timer = useRef(null);
  const active = run && (run.status === "pending" || run.status === "running");

  const poll = id => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/admin/ingest/status?run_id=${id}`, { credentials: "include" });
        const d = await r.json();
        setRun(d.run);
        if (d.run && (d.run.status === "pending" || d.run.status === "running")) poll(id);
        else if (onDone) onDone();
      } catch { poll(id); }
    }, 3000);
  };

  useEffect(() => {
    fetch(`${API}/admin/ingest/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.run) { setRun(d.run); if (d.run.status === "pending" || d.run.status === "running") poll(d.run.id); } })
      .catch(() => {});
    return () => clearTimeout(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/ingest/run`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setRun(d.run);
      poll(d.run.id);
    } catch { setErr("Käivitamine ebaõnnestus."); }
    setBusy(false);
  };

  const failed = run && run.status === "failed";
  const statusText = !run ? "" :
    run.status === "pending" ? "Ootab ingestorit…" :
    run.status === "running" ? "Sünkroonib…" :
    `${failed ? "Osaliselt ebaõnnestus" : "Valmis"} ${run.finished_at || ""}${run.requested_by ? ` · ${run.requested_by}` : ""}`;

  return (
    <div style={{ background: t.panel, border: `1px solid ${failed ? t.error : t.border}`, borderRadius: 12, padding: mobile ? "12px 14px" : "12px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={start} disabled={busy || active} style={{
          fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 7, border: "none", fontFamily: "inherit",
          background: busy || active ? t.border : ACCENT, color: "#fff", cursor: busy || active ? "default" : "pointer",
        }}>{active ? "⟳ Sünkroonib…" : "⟳ Sünkrooni"}</button>
        <span style={{ fontSize: 12.5, color: failed ? t.error : t.muted }}>{err || statusText || "Tõmbab kohe Waybilleri, Verstoni, Infrafly ja Navireci andmed."}</span>
        {run && run.result && run.result.length > 0 && !active && (
          <button onClick={() => setShowDetails(v => !v)} style={{ all: "unset", cursor: "pointer", fontSize: 12, color: t.muted, marginLeft: mobile ? 0 : "auto" }}>
            {showDetails ? "▼ Peida üksikasjad" : "▶ Üksikasjad"}
          </button>
        )}
      </div>
      {showDetails && run && run.result && run.result.length > 0 && !active && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: 12, color: t.muted }}>
          {run.result.map((r, i) => (
            <span key={i} style={{ color: r.ok ? t.muted : t.error }}>
              {r.ok ? "✓" : "✕"} <b style={{ color: r.ok ? t.text : t.error }}>{SOURCE_NAMES[r.source] || r.source}</b> {r.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Module({ t, mobile, label, open, onToggle, excel, children }) {
  return (
    <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12, padding: mobile ? "14px 16px" : "16px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <button onClick={onToggle} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: mobile ? 8 : 10, minWidth: 0 }}>
          <span style={{ fontSize: mobile ? 9 : 10, color: open ? t.text : t.mutedLight, flexShrink: 0 }}>{open ? "▼" : "▶"}</span>
          <span style={{
            fontSize: mobile ? 11 : 12.5, fontWeight: open ? 700 : 500, letterSpacing: "0.05em", textTransform: "uppercase",
            color: open ? t.text : t.mutedLight, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{label}</span>
        </button>
        {!mobile && excel}
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${t.border}`, marginTop: mobile ? 12 : 14, paddingTop: mobile ? 12 : 14 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Empty({ t, mobile, text }) {
  return <div style={{ fontSize: mobile ? 12 : 13, color: t.mutedLight }}>{text}</div>;
}

function ImportPanel({ t, mobile, description, accept, endpoint, siteRequired, sites, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [importSite, setImportSite] = useState("");
  const disabled = busy || (siteRequired && !importSite);

  const handle = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    if (siteRequired) fd.append("site", importSite);
    try {
      const r = await fetch(`${API}/${endpoint}`, { method: "POST", body: fd });
      const res = await r.json();
      setResult(res);
      if (res.inserted > 0 || res.updated > 0) onDone();
    } catch {
      setResult({ error: "Import ebaõnnestus." });
    }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <div>
      <div style={{ fontSize: mobile ? 12 : 13, color: t.muted, marginBottom: 12 }}>{description}</div>
      {siteRequired && (
        <select value={importSite} onChange={e => setImportSite(e.target.value)} style={{
          display: "block", marginBottom: 12, maxWidth: 260, width: "100%", background: t.bg, color: t.text,
          border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit",
        }}>
          <option value="">— Vali objekt —</option>
          {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
      )}
      <label style={{
        display: "inline-block", background: disabled ? t.border : NAVY, color: disabled ? t.mutedLight : "#fff",
        padding: "8px 18px", borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
      }}>
        {busy ? "Importin..." : `Vali fail (${accept.split(",").join(" / ")})`}
        <input type="file" accept={accept} onChange={handle} style={{ display: "none" }} disabled={disabled} />
      </label>
      {result && !result.error && (
        <div style={{ marginTop: 12, fontSize: 13, color: t.text, fontWeight: 600 }}>
          ✓ Lisatud: {result.inserted}{result.updated !== undefined && ` · Uuendatud: ${result.updated}`} · Vahele jäetud: {result.skipped}
        </div>
      )}
      {result?.error && <div style={{ marginTop: 12, fontSize: 13, color: t.error }}>{result.error}</div>}
    </div>
  );
}

// ─── machine hours toggle panel ───

function MachineHoursPanel({ t, mobile, rows, expanded, toggleExpand }) {
  const byMachine = {};
  for (const r of rows) {
    const key = r.machine_name;
    if (!byMachine[key]) byMachine[key] = { name: key, type: r.machine_type, totalHours: 0, days: 0, rows: [] };
    byMachine[key].totalHours += r.hours;
    byMachine[key].days += 1;
    byMachine[key].rows.push(r);
  }
  const machines = Object.values(byMachine).sort((a, b) => b.totalHours - a.totalHours);

  const cellPad = mobile ? "6px 8px" : "6px 10px";
  const th = (align) => ({
    position: "sticky", top: 0, background: t.tableHead, textAlign: align, padding: cellPad,
    fontWeight: 600, color: t.muted, borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
    fontSize: mobile ? 10 : 10.5,
  });
  const td = (extra) => ({ padding: cellPad, color: t.text, borderBottom: `1px solid ${t.rowBorder}`, whiteSpace: "nowrap", ...extra });

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {machines.map(m => {
        const key = `mh::${m.name}`;
        const isOpen = !!expanded[key];
        const header = (
          <button onClick={() => toggleExpand(key)} style={{
            all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            padding: mobile ? "11px 0" : "12px 0", borderTop: `1px solid ${t.border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: mobile ? 7 : 8, minWidth: 0, flex: 1 }}>
              <Chevron open={isOpen} color={t.mutedLight} size={mobile ? 11 : 12} />
              <div style={{ fontSize: mobile ? 12.5 : 13.5, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, textAlign: "left" }}>{m.name}</div>
              <div style={{ fontSize: mobile ? 10.5 : 11.5, color: t.mutedLight, whiteSpace: "nowrap", flexShrink: 0 }}>{m.days} päeva</div>
            </div>
            <div style={{ fontSize: mobile ? 12.5 : 13.5, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>{fmt(m.totalHours, "h")}</div>
          </button>
        );

        return (
          <div key={key}>
            {header}
            {isOpen && (
              <div style={{ padding: mobile ? "4px 0 14px 17px" : "6px 0 16px 20px" }}>
                <div style={{ maxHeight: mobile ? 200 : 260, overflowY: "auto", border: `1px solid ${t.border}`, borderRadius: 8 }}>
                  <table style={{ width: "100%", fontSize: mobile ? 11 : 12, borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th("left")}>Kuupäev</th>
                        <th style={th("right")}>Tunnid</th>
                        <th style={th("left")}>Kirjeldus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.rows.map((r, i) => (
                        <tr key={i}>
                          <td style={td({ fontWeight: 500 })}>{r.date}</td>
                          <td style={td({ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" })}>{fmt(r.hours, "h")}</td>
                          <td style={td({ color: t.muted, overflow: "hidden", textOverflow: "ellipsis", maxWidth: mobile ? 120 : 250 })} title={r.operator}>{r.operator || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── page ───

export { DUP_FIELD_LABELS };

export default function BetaDashboard({ hiddenModules = [], showDupFlags = false }) {
  const mobile = useIsMobile();
  const themeCtx = useAppTheme();
  const [localDark, localToggle] = useLocalTheme();
  const dark = themeCtx ? themeCtx.dark : localDark;
  const toggleDark = themeCtx ? themeCtx.toggle : localToggle;
  const t = dark ? THEMES.dark : THEMES.light;
  const accentSoft = `color-mix(in srgb, ${ACCENT} 16%, ${t.panel})`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const authCtx = useAuth();
  const userSites = authCtx?.user?.sites || [];
  const isSiteLead = authCtx?.user?.role === "site_lead";
  const canHaulReport = ["admin", "site_lead"].includes(authCtx?.user?.role);
  const isAdmin = authCtx?.user?.role === "admin";
  const userSiteLocked = isSiteLead && userSites.length === 1 ? userSites[0].name : null;
  const [site, setSite] = useState(() => {
    if (userSiteLocked) return userSiteLocked;
    if (isSiteLead && userSites.length > 0) return userSites[0].name;
    try { return localStorage.getItem("dash_site") || ""; } catch { return ""; }
  });
  const [sites, setSites] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  const [expandedMaterials, setExpandedMaterials] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [openModules, setOpenModules] = useState(MODULE_DEFAULTS);

  useEffect(() => {
    if (!userSiteLocked) try { localStorage.setItem("dash_site", site); } catch {}
  }, [site, userSiteLocked]);

  const toggleMaterial = key => setExpandedMaterials(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleRows = key => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleModule = key => setOpenModules(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    document.body.style.background = t.bg;
    document.body.style.margin = "0";
  }, [t.bg]);

  useEffect(() => {
    fetch(`${API}/sites`).then(r => r.json()).then(setSites).catch(() => {});
  }, []);

  const params = new URLSearchParams({ range });
  if (range === "custom") { params.append("date_from", customFrom); params.append("date_to", customTo); }
  if (site) params.append("site", site);
  const paramStr = params.toString();

  useEffect(() => {
    if (range === "custom" && (!customFrom || !customTo)) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/dashboard/data?${paramStr}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError("Andmete laadimine ebaõnnestus."); setLoading(false); });
  }, [paramStr, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCustom = (from, to) => { setCustomFrom(from); setCustomTo(to); setRange("custom"); };

  // Data layer: zero-total materials and empty sources never reach the template
  const sources = data ? [
    buildSource("waybiller", "Waybiller veoselehed", data.waybiller.map(m => ({
      name: m.material, total: m.total, unit: m.rows[0]?.yhik || "t",
      rows: m.rows.map(r => ({ date: r.date, time: r.time, reg: r.reg, to: r.destination, qty: r.kogus, origin: r.origin, dup_score: r.dup_score || 0, dup_conflict: !!r.dup_conflict, _id: r._id, _source: "waybiller" })),
    })), "export/waybiller"),
    buildSource("verston", "Verston", (data.verston || []).map(f => ({
      name: f.fraction, total: f.total, unit: "t",
      rows: f.rows.map(r => ({ date: r.date, time: r.time, reg: r.reg, to: r.client, qty: r.net, origin: r.site, dup_score: r.dup_score || 0, dup_conflict: !!r.dup_conflict, _id: r._id, _source: "verston" })),
    })), "export/verston"),
    buildSource("operator", "Operaatori koormalogid",
      groupLoadsByMaterial(data.loads).map(m => ({
        name: m.material, total: m.totalLoads, unit: "koormaid",
        rows: m.rows.map(r => ({ date: r.date, reg: r.reg, type: r.vehicle_type, qty: r.loads })),
      })), "export/loads", {
        heroUnit: "koormaid",
        columns: [
          { header: "Kuupäev", field: "date", align: "left" },
          { header: "Reg", field: "reg", align: "left", accent: true },
          { header: "Sõidukitüüp", field: "type", align: "left", hideOnMobile: true },
          { header: "Koormat", field: "qty", align: "right", bold: true },
        ],
      }),
  ].filter(s => s.hasData) : [];

  const gap = mobile ? 12 : 14;

  return (
    <div style={{
      width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", background: t.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', transition: "background .15s ease",
    }}>
      <Header t={t} mobile={mobile} dark={dark} toggleDark={toggleDark}
        range={range} setRange={setRange} site={site} setSite={setSite}
        sites={isSiteLead ? userSites.map(s => ({ id: s.id, name: s.name })) : sites}
        label={data ? rangeLabel(data.range.from, data.range.to) : ""}
        customFrom={customFrom} customTo={customTo} applyCustom={applyCustom}
        onToggleSidebar={themeCtx ? themeCtx.onToggleSidebar : undefined}
        siteLocked={!!userSiteLocked} />

      <div style={{
        maxWidth: 1240, width: "100%", boxSizing: "border-box", margin: "0 auto",
        padding: mobile ? "16px 14px 48px" : "28px 24px 64px", display: "flex", flexDirection: "column", gap: mobile ? 16 : 22,
        opacity: loading && data ? 0.55 : 1, transition: "opacity .15s ease",
      }}>
        {loading && !data && <div style={{ color: t.muted, textAlign: "center", padding: 60, fontSize: 13 }}>Laadin...</div>}
        {error && <div style={{ color: t.error, textAlign: "center", padding: 24, fontSize: 13 }}>{error}</div>}

        {isAdmin && <IngestRunButton t={t} mobile={mobile} onDone={() => setReloadKey(k => k + 1)} />}

        {data && <>
          {sources.length > 0 ? (
            <div style={mobile
              ? { display: "flex", flexDirection: "column", gap: 14 }
              : { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 18, alignItems: "start" }}>
              {sources.map(src => (
                <SourceCard key={src.id} t={t} src={src} mobile={mobile} accentSoft={accentSoft}
                  expanded={expandedMaterials} toggleMaterial={toggleMaterial}
                  rowsOpen={expandedRows} toggleRows={toggleRows} exportParams={paramStr}
                  showDupFlags={showDupFlags} />
              ))}
            </div>
          ) : (
            <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 14, padding: "28px 24px", textAlign: "center", color: t.mutedLight, fontSize: 13 }}>
              Valitud perioodil andmed puuduvad.
            </div>
          )}

          {canHaulReport && (
            <Module t={t} mobile={mobile} label="Veod autode kaupa (PDF)" open={!!openModules.veod} onToggle={() => toggleModule("veod")}>
              {openModules.veod && <HaulReportPanel t={t} mobile={mobile} paramStr={paramStr} />}
            </Module>
          )}

          <Module t={t} mobile={mobile} label="Masinatunnid"
            open={openModules.masinatunnid !== undefined ? openModules.masinatunnid : (data.machine_hours && data.machine_hours.length > 0)}
            onToggle={() => toggleModule("masinatunnid")}>
            {(!data.machine_hours || data.machine_hours.length === 0) ? <Empty t={t} mobile={mobile} text="Andmed puuduvad." /> : (
              <MachineHoursPanel t={t} mobile={mobile} rows={data.machine_hours}
                expanded={expandedMaterials} toggleExpand={toggleMaterial} />
            )}
          </Module>

          <div style={{ display: "flex", flexDirection: "column", gap }}>
            <Module t={t} mobile={mobile} label="Töötunnid" open={openModules.tootunnid} onToggle={() => toggleModule("tootunnid")}>
              {data.worker_hours.length === 0 ? <Empty t={t} mobile={mobile} text="Andmed puuduvad." /> : (
                <DataTable t={t} mobile={mobile} columns={["Töötaja", "Päevi", "Tunnid kokku"]}
                  rows={data.worker_hours.map(w => [w.worker, w.days, `${w.total_hours} h`])} />
              )}
            </Module>

            {!hiddenModules.includes("paevaaruanded") && <Module t={t} mobile={mobile} label="Päevaaruanded" open={openModules.paevaaruanded} onToggle={() => toggleModule("paevaaruanded")}
              excel={<ExcelButton t={t} endpoint="export/reports" params={paramStr} />}>
              {data.reports.length === 0 ? <Empty t={t} mobile={mobile} text="Aruandeid pole." /> : data.reports.map((r, i) => (
                <div key={i} style={{ borderTop: i ? `1px solid ${t.rowBorder}` : "none", padding: i ? "12px 0 0" : 0, marginBottom: 12 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: mobile ? "4px 14px" : "4px 20px", alignItems: "baseline", marginBottom: 6, fontSize: mobile ? 12 : 13 }}>
                    <span style={{ fontWeight: 700, color: t.text }}>{r.date}</span>
                    <span style={{ color: t.text }}>{r.site}</span>
                    <span style={{ color: t.muted }}>{r.worker_count} töötajat</span>
                    <span style={{ color: t.muted }}>{r.weather_temp}°C · {r.weather_condition} · {r.weather_wind} m/s</span>
                  </div>
                  <div style={{ fontSize: mobile ? 12 : 13, color: t.text, marginBottom: 4 }}>
                    <span style={{ color: t.muted, fontWeight: 600, marginRight: 8 }}>Tööd:</span>{r.notes}
                  </div>
                  {r.materials_received && (
                    <div style={{ fontSize: mobile ? 12 : 13, color: t.text }}>
                      <span style={{ color: t.muted, fontWeight: 600, marginRight: 8 }}>Materjalid:</span>{r.materials_received}
                    </div>
                  )}
                </div>
              ))}
            </Module>}

            <Module t={t} mobile={mobile} label="Mehhanismid objektil" open={openModules.mehhanismid} onToggle={() => toggleModule("mehhanismid")}>
              {data.fleet.length === 0 ? <Empty t={t} mobile={mobile} text="Andmed puuduvad." /> : (
                <DataTable t={t} mobile={mobile} columns={["Masin", "Tüüp", "Objekt", "Saabumise kp", "Päevi"]}
                  rows={data.fleet.map(f => [f.machine_name, f.machine_type || "—", f.site, f.date_arrived, f.days])} />
              )}
            </Module>

            {!hiddenModules.includes("tarnelehed") && <Module t={t} mobile={mobile} label="Tarnelehed" open={openModules.tarnelehed} onToggle={() => toggleModule("tarnelehed")}
              excel={<ExcelButton t={t} endpoint="export/supplier" params={paramStr} />}>
              {data.supplier.length === 0 ? <Empty t={t} mobile={mobile} text="Tarneid pole." /> : (
                <DataTable t={t} mobile={mobile} columns={["Kuupäev", "Kell", "Materjal", "Ettevõte", "Reg", "Marsruut", "Kogus"]}
                  rows={data.supplier.flatMap(m => m.rows.map(r => [r.date, r.time, m.material, r.company, r.reg, r.marsruut || r.site, `${r.kogus} ${r.yhik}`]))} />
              )}
            </Module>}

            {!isSiteLead && <Module t={t} mobile={mobile} label="Impordi Remato tunnitabel" open={openModules.import_remato} onToggle={() => toggleModule("import_remato")}>
              <ImportPanel t={t} mobile={mobile} description="Laadi üles Remato detailne aruanne (.xlsx) töötundide uuendamiseks."
                accept=".xlsx" endpoint="worker-hours/import" sites={sites} onDone={() => setReloadKey(k => k + 1)} />
            </Module>}

            <Module t={t} mobile={mobile} label="Impordi Waybiller veoselehed" open={openModules.import_waybiller} onToggle={() => toggleModule("import_waybiller")}>
              <ImportPanel t={t} mobile={mobile} description="Laadi üles Waybilleri veoselehtede eksport (.xlsx või .csv) andmete uuendamiseks."
                accept=".xlsx,.csv" endpoint="waybiller-deliveries/import" siteRequired sites={isSiteLead ? userSites : sites} onDone={() => setReloadKey(k => k + 1)} />
            </Module>
          </div>
        </>}
      </div>
    </div>
  );
}
